import { useEffect, useState } from "react";
import { Phone, X } from "lucide-react";
import { COMPANY, buildWhatsAppLink } from "../../lib/constants";

export const FloatingWhatsApp = () => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => {
            if (!dismissed) setShowTooltip(true);
        }, 3500);
        return () => clearTimeout(t);
    }, [dismissed]);

    return (
        <>
            {/* Floating WhatsApp button */}
            <div className="fixed bottom-6 right-4 md:right-6 z-50 flex flex-col items-end gap-3">
                {showTooltip && !dismissed && (
                    <div
                        data-testid="whatsapp-tooltip"
                        className="relative bg-white text-black p-4 pr-9 max-w-[260px] rounded-xl shadow-2xl border-l-4 border-[#25D366] fade-up"
                    >
                        <button
                            onClick={() => {
                                setShowTooltip(false);
                                setDismissed(true);
                            }}
                            className="absolute top-2 right-2 text-gray-400 hover:text-black"
                            aria-label="Fechar"
                            data-testid="whatsapp-tooltip-close"
                        >
                            <X size={16} />
                        </button>
                        <div className="text-xs font-bold uppercase tracking-wider text-[#25D366] mb-1">
                            ● Online agora
                        </div>
                        <div className="text-sm font-bold leading-snug">
                            Precisa de guincho? Chame agora — respondemos em
                            segundos.
                        </div>
                    </div>
                )}

                <a
                    href={buildWhatsAppLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="floating-whatsapp-btn"
                    aria-label="Falar no WhatsApp"
                    className="relative flex items-center justify-center w-16 h-16 bg-[#25D366] hover:bg-[#1ebd5c] text-white rounded-full shadow-2xl transition-all duration-300 hover:scale-110 animate-whatsapp"
                >
                    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                </a>
            </div>

            {/* Mobile sticky bottom CTA bar */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 cta-bar-shadow">
                <div className="grid grid-cols-2">
                    <a
                        href={COMPANY.phoneTel}
                        data-testid="mobile-bar-phone-btn"
                        className="flex items-center justify-center gap-2 py-4 bg-[#f45100] text-white font-bold text-sm uppercase tracking-wider"
                    >
                        <Phone size={18} strokeWidth={2.5} />
                        Ligar
                    </a>
                    <a
                        href={buildWhatsAppLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="mobile-bar-whatsapp-btn"
                        className="flex items-center justify-center gap-2 py-4 bg-[#25D366] text-white font-bold text-sm uppercase tracking-wider"
                    >
                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        WhatsApp
                    </a>
                </div>
            </div>
        </>
    );
};
